/**
 * TeamSystem Impact - IRO API Client
 * API calls for the new IRO Registry
 */

import { apiClient } from '../../../api/client';
import { AxiosError } from 'axios';

/**
 * Helper to extract error message from API response
 */
function extractErrorMessage(error: unknown, defaultMessage: string): string {
  if (error instanceof AxiosError && error.response?.data) {
    const data = error.response.data as { error?: string; message?: string };
    return data.error || data.message || defaultMessage;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return defaultMessage;
}

// Local type definitions to avoid shared package dependency issues

export type ESRSStandard = 'E1' | 'E2' | 'E3' | 'E4' | 'E5' | 'S1' | 'S2' | 'S3' | 'S4' | 'G1';
export type IROType = 'impact' | 'risk' | 'opportunity';
export type IROStatus = 'draft' | 'in_validation' | 'validated' | 'material' | 'not_material';
export type ImpactNature = 'actual' | 'potential';
export type ImpactDirection = 'positive' | 'negative';
export type RiskType = 'physical' | 'transition' | 'systemic';
export type OpportunityType = 'resource_efficiency' | 'products_services' | 'markets' | 'resilience' | 'reputation';
export type TimeHorizon = 'short' | 'medium' | 'long';
export type TeamSystemCountry = 'IT' | 'ES' | 'FR' | 'CH' | 'AL' | 'DK' | 'TR' | 'IL';
export type ValueChainPosition = 'upstream' | 'operations' | 'downstream';
export type StakeholderCategory = 'employees' | 'customers' | 'suppliers' | 'investors' | 'local_communities' | 'regulators' | 'partners' | 'other';

export interface GRITopicMapping {
  topicCode: string;
  topicName: string;
  disclosures: string[];
}

export interface ImpactMaterialityAssessment {
  scale: number;
  scope: number;
  irremediability?: number;
  probability?: number;
  impactScore: number;
  notes?: string;
  evidenceRefs?: string[];
  assessedBy?: string;
  assessedByName?: string;
  assessedAt?: string;
}

export interface FinancialMaterialityAssessment {
  likelihood: number;
  ecoFinancialImpact?: number;
  reputationalImpact: number;
  operationalImpact: number;
  rmMaturityLevel?: number;
  financialScore: number;
  notes?: string;
  evidenceRefs?: string[];
  assessedBy?: string;
  assessedByName?: string;
  assessedAt?: string;
}

export interface ConsolidatedScores {
  impactScore: number;
  financialScore: number;
  overallScore: number;
  impactAssessmentCount: number;
  financialAssessmentCount: number;
  lastConsolidatedAt: string;
}

export interface ScoreAdjustmentHistoryEntry {
  impactScore?: number;
  financialScore?: number;
  rationale?: string;
  adjustedBy: string;
  adjustedByName: string;
  adjustedAt: string;
  removedAt?: string;
  removedBy?: string;
  removedByName?: string;
}

export interface ScoreAdjustment {
  active: boolean;
  impactScore?: number;
  financialScore?: number;
  rationale?: string;
  adjustedBy: string;
  adjustedByName: string;
  adjustedAt: string;
  previousImpactScore: number;
  previousFinancialScore: number;
  history: ScoreAdjustmentHistoryEntry[];
}

export interface AssignedStakeholder {
  userId: string;
  userName: string;
  userEmail: string;
  assignedAt?: string;
  assignedBy?: string;
}

export interface InternalStakeholder {
  _id: string;
  nome: string;
  cognome: string;
  nomeCompleto: string;
  email: string;
  ruolo: string;
  ambiti: string[];
}

export interface MacroCategoryPopulated {
  _id: string;
  code: string;
  name: string;
  nameEN: string;
}

export interface IRO {
  _id?: string;
  aziendaId: string;
  assessmentYear: number;
  code: string;
  name: string;
  nameEN?: string;
  description: string;
  descriptionEN?: string;
  type: IROType;
  esrsStandard: ESRSStandard;
  esrsSubTopic?: string;
  griTopicMapping?: GRITopicMapping;
  macroCategoryId?: string | MacroCategoryPopulated;
  assignedStakeholders?: AssignedStakeholder[];
  geographies: TeamSystemCountry[];
  valueChainPositions: ValueChainPosition[];
  timeHorizon: TimeHorizon;
  affectedStakeholders: StakeholderCategory[];
  impactNature?: ImpactNature;
  impactDirection?: ImpactDirection;
  riskType?: RiskType;
  opportunityType?: OpportunityType;
  impactMaterialityAssessment?: ImpactMaterialityAssessment;
  financialMaterialityAssessment?: FinancialMaterialityAssessment;
  scores: {
    impactScore: number;
    financialScore: number;
    overallScore: number;
  };
  // Multi-stakeholder assessment fields
  consolidatedScores?: ConsolidatedScores;
  hasMultiStakeholderAssessment?: boolean;
  // Score adjustment
  scoreAdjustment?: ScoreAdjustment;
  // Assessment progress (populated by list endpoint)
  assessmentProgress?: { total: number; completed: number };
  status: IROStatus;
  materialityDecision?: 'material' | 'not_material';
  materialityRationale?: string;
  linkedPolicies?: string[];
  linkedTargets?: string[];
  linkedActionPlans?: string[];
  linkedEvidences?: string[];
  validatedBy?: string;
  validatedByName?: string;
  validatedAt?: string;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: string;
  approvalOrganId?: string;
  auditLog?: Array<{
    id: string;
    timestamp: string;
    userId: string;
    userName: string;
    action: string;
    field?: string;
    previousValue?: any;
    newValue?: any;
    comments?: string;
  }>;
  version: number;
  previousVersionId?: string;
  createdBy: string;
  createdByName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateIRODTO {
  assessmentYear: number;
  name: string;
  nameEN?: string;
  description: string;
  descriptionEN?: string;
  type: IROType;
  esrsStandard: ESRSStandard;
  esrsSubTopic?: string;
  griTopicMapping?: GRITopicMapping;
  macroCategoryId?: string;
  assignedStakeholders?: AssignedStakeholder[];
  geographies: TeamSystemCountry[];
  valueChainPositions: ValueChainPosition[];
  timeHorizon: TimeHorizon;
  affectedStakeholders: StakeholderCategory[];
  impactNature?: ImpactNature;
  impactDirection?: ImpactDirection;
  riskType?: RiskType;
  opportunityType?: OpportunityType;
}

export interface UpdateIRODTO extends Partial<CreateIRODTO> {
  status?: IROStatus;
  materialityRationale?: string;
}

export interface AssessImpactMaterialityDTO {
  scale: number;
  scope: number;
  irremediability?: number;
  probability?: number;
  notes?: string;
  evidenceRefs?: string[];
}

export interface AssessFinancialMaterialityDTO {
  likelihood: number;
  ecoFinancialImpact?: number;
  reputationalImpact: number;
  operationalImpact: number;
  rmMaturityLevel?: number;
  notes?: string;
  evidenceRefs?: string[];
}

export interface IROFilters {
  assessmentYear?: number;
  type?: IROType;
  esrsStandard?: ESRSStandard;
  status?: IROStatus;
  geographies?: TeamSystemCountry[];
  valueChainPositions?: ValueChainPosition[];
  materialityDecision?: 'material' | 'not_material';
  riskType?: RiskType;
  opportunityType?: OpportunityType;
  search?: string;
}

export interface IROListResponse {
  items: IRO[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface IROStats {
  total: number;
  assessedTotal: number;
  byType: {
    impact: number;
    risk: number;
    opportunity: number;
  };
  byStatus: {
    draft: number;
    in_validation: number;
    validated: number;
    material: number;
    not_material: number;
  };
  byESRS: Record<ESRSStandard, number>;
  materialCount: number;
  averageScores: {
    impactScore: number;
    financialScore: number;
  };
}

const BASE_PATH = '/dma/iro';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface MatrixDataResponse {
  items: Array<{
    id: string;
    code: string;
    name: string;
    nameEN?: string;
    type: IROType;
    esrsStandard: string;
    impactScore: number;
    financialScore: number;
    isMaterial: boolean;
    materialityDecision?: string;
    hasConsolidatedScores?: boolean;
  }>;
  thresholds: {
    impactScore: number;
    financialScore: number;
  };
  aggregations: {
    byESRS: Record<string, { count: number; avgImpact: number; avgFinancial: number; materialCount: number }>;
    byType: Record<string, { count: number; avgImpact: number; avgFinancial: number; materialCount: number }>;
    byGeography: Record<string, { count: number; avgImpact: number; avgFinancial: number; materialCount: number }>;
  };
}

export const iroApi = {
  create: async (data: CreateIRODTO): Promise<IRO> => {
    try {
      const response = await apiClient.post<ApiResponse<IRO>>(`${BASE_PATH}`, data);
      if (!response.data.success) throw new Error(response.data.error || 'Failed to create IRO');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante la creazione dell\'IRO'));
    }
  },

  list: async (filters?: IROFilters & { page?: number; pageSize?: number }): Promise<IROListResponse> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            params.append(key, value.join(','));
          } else {
            params.append(key, String(value));
          }
        }
      });
    }
    const response = await apiClient.get<ApiResponse<IROListResponse>>(`${BASE_PATH}?${params.toString()}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to list IROs');
    return response.data.data!;
  },

  getById: async (id: string): Promise<IRO> => {
    const response = await apiClient.get<ApiResponse<IRO>>(`${BASE_PATH}/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'IRO not found');
    return response.data.data!;
  },

  getByCode: async (code: string): Promise<IRO> => {
    const response = await apiClient.get<ApiResponse<IRO>>(`${BASE_PATH}/code/${code}`);
    if (!response.data.success) throw new Error(response.data.error || 'IRO not found');
    return response.data.data!;
  },

  update: async (id: string, data: UpdateIRODTO): Promise<IRO> => {
    try {
      const response = await apiClient.put<ApiResponse<IRO>>(`${BASE_PATH}/${id}`, data);
      if (!response.data.success) throw new Error(response.data.error || 'Failed to update IRO');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante l\'aggiornamento dell\'IRO'));
    }
  },

  delete: async (id: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<void>>(`${BASE_PATH}/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to delete IRO');
  },

  getStats: async (assessmentYear?: number): Promise<IROStats> => {
    const params = assessmentYear ? `?assessmentYear=${assessmentYear}` : '';
    const response = await apiClient.get<ApiResponse<IROStats>>(`${BASE_PATH}/stats${params}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to get stats');
    return response.data.data!;
  },

  listMaterial: async (assessmentYear: number): Promise<IRO[]> => {
    const response = await apiClient.get<ApiResponse<IRO[]>>(`${BASE_PATH}/material?assessmentYear=${assessmentYear}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to list material IROs');
    return response.data.data!;
  },

  assessImpact: async (id: string, data: AssessImpactMaterialityDTO): Promise<IRO> => {
    try {
      const response = await apiClient.post<ApiResponse<IRO>>(`${BASE_PATH}/${id}/impact-assessment`, data);
      if (!response.data.success) throw new Error(response.data.error || 'Failed to assess impact');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante la valutazione impatto'));
    }
  },

  assessFinancial: async (id: string, data: AssessFinancialMaterialityDTO): Promise<IRO> => {
    try {
      const response = await apiClient.post<ApiResponse<IRO>>(`${BASE_PATH}/${id}/financial-assessment`, data);
      if (!response.data.success) throw new Error(response.data.error || 'Failed to assess financial');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante la valutazione finanziaria'));
    }
  },

  submitForValidation: async (id: string): Promise<IRO> => {
    const response = await apiClient.post<ApiResponse<IRO>>(`${BASE_PATH}/${id}/submit-validation`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to submit');
    return response.data.data!;
  },

  validate: async (id: string): Promise<IRO> => {
    const response = await apiClient.post<ApiResponse<IRO>>(`${BASE_PATH}/${id}/validate`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to validate');
    return response.data.data!;
  },

  reject: async (id: string, reason: string): Promise<IRO> => {
    const response = await apiClient.post<ApiResponse<IRO>>(`${BASE_PATH}/${id}/reject`, { reason });
    if (!response.data.success) throw new Error(response.data.error || 'Failed to reject');
    return response.data.data!;
  },

  approveMateriality: async (
    id: string,
    decision: 'material' | 'not_material',
    rationale: string,
    organId?: string
  ): Promise<IRO> => {
    const response = await apiClient.post<ApiResponse<IRO>>(`${BASE_PATH}/${id}/approve-materiality`, {
      decision, rationale, organId,
    });
    if (!response.data.success) throw new Error(response.data.error || 'Failed to approve');
    return response.data.data!;
  },

  clone: async (id: string, newYear: number): Promise<IRO> => {
    const response = await apiClient.post<ApiResponse<IRO>>(`${BASE_PATH}/${id}/clone`, { newYear });
    if (!response.data.success) throw new Error(response.data.error || 'Failed to clone');
    return response.data.data!;
  },

  duplicate: async (id: string): Promise<IRO> => {
    try {
      const response = await apiClient.post<ApiResponse<IRO>>(`${BASE_PATH}/${id}/duplicate`);
      if (!response.data.success) throw new Error(response.data.error || 'Failed to duplicate');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante la duplicazione dell\'IRO'));
    }
  },

  bulkDelete: async (ids: string[]): Promise<{ deleted: number }> => {
    try {
      const response = await apiClient.post<ApiResponse<{ deleted: number }>>(`${BASE_PATH}/bulk-delete`, { ids });
      if (!response.data.success) throw new Error(response.data.error || 'Failed to bulk delete');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante l\'eliminazione massiva'));
    }
  },

  getMatrixData: async (
    assessmentYear: number,
    filters?: {
      esrsStandard?: string;
      type?: string;
      geographies?: string[];
      valueChainPositions?: string[];
    }
  ): Promise<MatrixDataResponse> => {
    const params = new URLSearchParams();
    params.append('assessmentYear', String(assessmentYear));
    if (filters) {
      if (filters.esrsStandard) params.append('esrsStandard', filters.esrsStandard);
      if (filters.type) params.append('type', filters.type);
      if (filters.geographies?.length) params.append('geographies', filters.geographies.join(','));
      if (filters.valueChainPositions?.length) params.append('valueChainPositions', filters.valueChainPositions.join(','));
    }
    const response = await apiClient.get<ApiResponse<MatrixDataResponse>>(`${BASE_PATH}/matrix?${params.toString()}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to get matrix');
    return response.data.data!;
  },

  updateThresholds: async (thresholds: { impactScore: number; financialScore: number; assessmentYear?: number }): Promise<void> => {
    const response = await apiClient.put<ApiResponse<void>>(`${BASE_PATH}/thresholds`, thresholds);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update thresholds');
  },

  exportForExcel: async (assessmentYear: number): Promise<any[]> => {
    const response = await apiClient.get<ApiResponse<any[]>>(`${BASE_PATH}/export?assessmentYear=${assessmentYear}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to export');
    return response.data.data!;
  },

  importFromExcel: async (
    file: File,
    assessmentYear: number,
    replaceExisting: boolean = false
  ): Promise<{ total: number; created: number; updated: number; errors: number; errorDetails: { code: string; error: string }[] }> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post<ApiResponse<any>>(
        `${BASE_PATH}/import?assessmentYear=${assessmentYear}&replaceExisting=${replaceExisting}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      if (!response.data.success) throw new Error(response.data.error || 'Failed to import');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante l\'importazione'));
    }
  },

  adjustScores: async (id: string, data: { impactScore?: number; financialScore?: number; rationale?: string }): Promise<IRO> => {
    try {
      const response = await apiClient.post<ApiResponse<IRO>>(`${BASE_PATH}/${id}/score-adjustment`, data);
      if (!response.data.success) throw new Error(response.data.error || 'Failed to adjust scores');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante l\'aggiustamento dei punteggi'));
    }
  },

  removeScoreAdjustment: async (id: string): Promise<IRO> => {
    try {
      const response = await apiClient.delete<ApiResponse<IRO>>(`${BASE_PATH}/${id}/score-adjustment`);
      if (!response.data.success) throw new Error(response.data.error || 'Failed to remove adjustment');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante la rimozione dell\'aggiustamento'));
    }
  },

  recalculateAllScores: async (assessmentYear: number): Promise<{
    processed: number;
    material: number;
    notMaterial: number;
    noAssessments: number;
    details: Array<{ iroCode: string; iroId: string; impactScore: number; financialScore: number; decision: string }>;
  }> => {
    try {
      const response = await apiClient.post<ApiResponse<any>>(
        `${BASE_PATH}/recalculate-all-scores`,
        { assessmentYear }
      );
      if (!response.data.success) throw new Error(response.data.error || 'Failed to recalculate');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante il consolidamento dei punteggi'));
    }
  },

  listStakeholders: async (filters?: { search?: string; role?: string }): Promise<InternalStakeholder[]> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.role) params.append('role', filters.role);
    const queryString = params.toString();
    const response = await apiClient.get<ApiResponse<InternalStakeholder[]>>(
      `${BASE_PATH}/stakeholders${queryString ? `?${queryString}` : ''}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to list stakeholders');
    return response.data.data!;
  },
};
